import React, { useCallback, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { getDataWithComp, getAlert, formatCurrency } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";

export default function ExportButton({ current, startDate, endDate, sums }) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const handleExport = useCallback(async (mode = "detail") => {
    try {
      setIsExporting(true);
      setExportProgress(10);

      const workbook = new ExcelJS.Workbook();
workbook.creator = "Encotech Co., Ltd.";
      workbook.created = new Date();

      const thin = { style: "thin", color: { argb: "FFCCCCCC" } };
      const borderAll = { top: thin, left: thin, bottom: thin, right: thin };
      const styles = {
        title: {
          font: { bold: true, size: 12, color: { argb: "FF2563EB" } },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } },
          alignment: { horizontal: "center", vertical: "middle" },
          border: borderAll,
        },
        cell: (col) => ({
          border: borderAll,
          numFmt: col >= 4 ? "#,##0.00" : undefined,
          alignment:
            col >= 4
              ? { horizontal: "right", vertical: "middle" }
              : { horizontal: "left", vertical: "middle" },
          font: { size: 11 },
        }),
        sumRow: {
          font: { bold: true, size: 11, color: { argb: "FF059669" } },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFECFDF5" } },
          alignment: { horizontal: "right", vertical: "middle" },
          border: borderAll,
          numFmtCols: [4],
        },
      };

      setExportProgress(30);

      if (mode === "summary") {
        // Worksheet for summary
        const ws = workbook.addWorksheet("Top Selling", {
          views: [{ state: "frozen", ySplit: 1 }],
        });

        // Title row
        const titleRow = ws.addRow([
          `รายงานลูกค้าซื้อซ้ำ (${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")})`,
        ]);
        titleRow.font = { bold: true, size: 14, color: { argb: "FF1E40AF" } };
        titleRow.alignment = { horizontal: "center" };
        ws.mergeCells(1, 1, 1, SUMMARY_FIELDS.length);

        // ---- Metrics rows ----
        ws.addRow([]); // blank row
        // Labels
        const metricsLabels = ["จำนวนลูกค้าทั้งหมด", "ลูกค้าซื้อซ้ำ", "อัตราการซื้อซ้ำ"];
        const labelRow = ws.addRow(metricsLabels);
        labelRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left" };
        });
        // Values
        const repeatRatePercent = (sums.repeat_rate * 100).toFixed(2) + " %";
        const valueRow = ws.addRow([
          sums.total_customer,
          sums.total_customer_repeat,
          repeatRatePercent,
        ]);
        valueRow.eachCell((cell, idx) => {
          if (idx < 3) {
            cell.numFmt = "#,##0";
            cell.alignment = { horizontal: "right" };
          } else {
            cell.alignment = { horizontal: "right" };
          }
        });
        ws.addRow([]); // blank before table

        setExportProgress(40);

        // Header of table
        const header = ws.addRow(SUMMARY_FIELDS.map((f) => f.label));
        header.eachCell((cell, idx) => {
          Object.assign(cell, styles.title, styles.cell(idx - 1));
        });

        setExportProgress(50);

        // Data rows
        current.forEach((item, index) => {
          const row = ws.addRow(SUMMARY_FIELDS.map((f) => item[f.key]));
          row.eachCell((cell, idx) =>
            Object.assign(cell, styles.cell(idx - 1))
          );
          if (index % 10 === 0) {
            setExportProgress(50 + (index / current.length) * 30);
          }
        });

        // Total row
        const totalRow = ws.addRow([
          "รวมทั้งหมด",
          ...Array(SUMMARY_FIELDS.length - 2).fill(""),
          formatCurrency(
            current.reduce((sum, item) => sum + (item.NetTotal || 0), 0)
          ),
        ]);
        totalRow.eachCell((cell) => Object.assign(cell, styles.sumRow));

        // Column widths
        ws.columns.forEach((col, i) => (col.width = [15, 30, 18][i] || 18));
      } else {
        // Detail mode (unchanged)
        const ws = workbook.addWorksheet("Top Selling Detail", {
          views: [{ state: "frozen", ySplit: 1 }],
        });

        // Main title
        const mainTitleRow = ws.addRow([
          `รายงานลูกค้าซื้อซ้ำ (${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")})`,
        ]);
        mainTitleRow.font = { bold: true, size: 14, color: { argb: "FF1E40AF" } };
        mainTitleRow.alignment = { horizontal: "center" };
        ws.mergeCells(1, 1, 1, DETAIL_FIELDS.length);
        ws.addRow([]);

        setExportProgress(40);

        for (const [custIndex, cust] of current.entries()) {
          const titleRow = ws.addRow([`${cust.Item_Code} - ${cust.Item_Name}`]);
          titleRow.font = { bold: true, size: 12, color: { argb: "FF7C3AED" } };
          titleRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF3E8FF" },
          };
          ws.mergeCells(titleRow.number, 1, titleRow.number, DETAIL_FIELDS.length);

          const detailHeader = ws.addRow(DETAIL_FIELDS.map((f) => f.label));
          detailHeader.eachCell((cell, idx) =>
            Object.assign(cell, styles.title, styles.cell(idx - 1))
          );

          const filter = [
            `AND Item_Code ='${cust.Item_Code}'`,
            `AND Doc_Date >= '${startDate}'`,
            `AND Doc_Date <= '${endDate}'`,
            "ORDER BY Doc_Date",
          ].join(" ");
          const details = (await getDataWithComp("API_0702_SO_D", filter)) || [];

          details.forEach((d) => {
            const values = [
              moment(d.Doc_Date).format("DD/MM/YYYY"),
              d.Doc_No,
              d.AR_Code,
              d.AR_Name,
              d.Item_Qty || 0,
              d.Item_Price_Unit || 0,
              d.Item_Discount || 0,
              d.Item_Total || 0,
            ];
            const row = ws.addRow(values);
            row.eachCell((cell, idx) =>
              Object.assign(cell, styles.cell(idx - 1))
            );
          });

          const sumRow = ws.addRow([
            "รวมทั้งหมด",
            "",
            "",
            "",
            "",
            "",
            "",
            details.reduce((sum, d) => sum + (d.Item_Total || 0), 0),
          ]);
          sumRow.eachCell((cell) =>
            Object.assign(cell, styles.sumRow)
          );

          ws.columns.forEach((col, i) => (col.width = [15, 18, 18, 30, 15, 12, 18, 15][i]));
          ws.addRow([]);

          setExportProgress(40 + ((custIndex + 1) / current.length) * 40);
        }
      }

      setExportProgress(90);

      // Write and save file
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Top_Selling_Products_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;

      saveAs(
        new Blob([buffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );

      setExportProgress(100);
      setTimeout(() => {
        getAlert("SUCCESS", `ส่งออกไฟล์ ${fileName} เรียบร้อยแล้ว`);
        setIsExporting(false);
        setExportProgress(0);
      }, 500);
    } catch (err) {
      setIsExporting(false);
      setExportProgress(0);
      getAlert("FAILED", `เกิดข้อผิดพลาด: ${err.message}`);
    }
  }, [current, startDate, endDate, sums]);

  return (
    <div className="d-flex justify-content-end align-items-center mb-3">
      {/* Export Progress */}
      {isExporting && (
        <div className="me-3" style={{ minWidth: "200px" }}>
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm text-success me-2"
              role="status"
            >
              <span className="visually-hidden">กำลังส่งออก...</span>
            </div>
            <div className="flex-grow-1">
              <div className="progress" style={{ height: "6px" }}>
                <div
                  className="progress-bar bg-gradient"
                  style={{
                    width: `${exportProgress}%`,
                    background: "linear-gradient(90deg, #10b981, #059669)",
                  }}
                ></div>
              </div>
              <small className="text-muted">
                {exportProgress.toFixed(0)}% กำลังส่งออก...
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Export Button Group */}
      <div className="btn-group shadow-sm">
     <button
          type="button"
          className="btn btn-sm btn-outline-orange me-2 btn-export-dropdown  dropdown-toggle "
          data-bs-toggle="dropdown"
          disabled={isExporting}
        >
          <i className="fas fa-file-excel me-1"></i>
          Export Excel
        </button>
        <ul
          className="dropdown-menu dropdown-menu-end shadow-lg border-0"
          style={{ minWidth: "200px" }}
        >
          <li className="px-3 py-2 border-bottom">
            <small className="text-muted fw-semibold">
              เลือกรูปแบบการส่งออก
            </small>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center py-2 px-3"
              onClick={() => handleExport("detail")}
              disabled={isExporting}
              style={{ transition: "all 0.2s ease" }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f3f4f6";
                e.target.style.paddingLeft = "20px";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "";
                e.target.style.paddingLeft = "12px";
              }}
            >
              <i className="fas fa-list-ul text-primary me-2"></i>
              <div>
                <div className="fw-medium">Export แบบละเอียด</div>
                <small className="text-muted">รายละเอียดทุกรายการซื้อ</small>
              </div>
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center py-2 px-3"
              onClick={() => handleExport("summary")}
              disabled={isExporting}
              style={{ transition: "all 0.2s ease" }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f3f4f6";
                e.target.style.paddingLeft = "20px";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "";
                e.target.style.paddingLeft = "12px";
              }}
            >
              <i className="fas fa-chart-bar text-success me-2"></i>
              <div>
                <div className="fw-medium">Export แบบสรุป</div>
                <small className="text-muted">สรุปยอดรวม</small>
              </div>
            </button>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li className="px-3 py-2">
            <div className="d-flex align-items-center text-muted">
              <i className="fas fa-info-circle me-2"></i>
              <small>รวม {current?.length || 0} รายการ</small>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
